import { useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";

const EVENT_TYPES = ["Wedding Catering", "Private Chef Dinner", "Corporate Catering", "Holiday Catering", "Drop & Geaux"];
const CUISINES = ["Cajun", "Creole", "Seafood", "Southern", "Vegetarian/Vegan", "Gluten-Free", "Other"];
const STYLES = ["Plated", "Buffet", "Chef Stations", "Passed Hors d'Oeuvres", "Drop-Off"];
const REFERRALS = ["Google", "Instagram", "Referral", "Venue", "Other"];

const inputCls = "w-full bg-white border border-brass/15 rounded-sm px-4 py-3 text-sm text-ink placeholder:text-ink2/50 focus:outline-none focus:ring-1 focus:ring-brass transition-shadow";
const labelCls = "block text-xs font-mono uppercase tracking-[0.25em] text-ink2 mb-2";

export default function ContactForm({ section }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: EVENT_TYPES[0], cuisine: CUISINES[0], service_style: STYLES[0], event_date: "", guests: "", referral: REFERRALS[0], message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", form);
      toast.success("Request received — Chef Blaine will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", event_type: EVENT_TYPES[0], cuisine: CUISINES[0], service_style: STYLES[0], event_date: "", guests: "", referral: REFERRALS[0], message: "" });
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="request" className="mx-auto max-w-4xl px-6 sm:px-12 py-24 sm:py-32" data-testid="event-request-section">
      <p className="text-xs uppercase font-mono tracking-[0.3em] text-gold text-center">{section.eyebrow || "Get in Touch"}</p>
      <h2 className="mt-4 text-3xl sm:text-4xl font-serif font-semibold text-ink text-center">{section.title || "Make an Event Request"}</h2>
      <form onSubmit={submit} className="mt-12 grid gap-6 sm:grid-cols-2" data-testid="event-request-form">
        <div><label className={labelCls}>Name *</label><input required value={form.name} onChange={set("name")} className={inputCls} data-testid="form-name-input" placeholder="Your name" /></div>
        <div><label className={labelCls}>Email *</label><input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="form-email-input" placeholder="you@email.com" /></div>
        <div><label className={labelCls}>Phone</label><input value={form.phone} onChange={set("phone")} className={inputCls} data-testid="form-phone-input" placeholder="(555) 555-5555" /></div>
        <div><label className={labelCls}>Event Date</label><input type="date" value={form.event_date} onChange={set("event_date")} className={inputCls} data-testid="form-date-input" /></div>
        <div>
          <label className={labelCls}>Event Type</label>
          <select value={form.event_type} onChange={set("event_type")} className={inputCls} data-testid="form-event-type-select">
            {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Cuisine</label>
          <select value={form.cuisine} onChange={set("cuisine")} className={inputCls} data-testid="form-cuisine-select">
            {CUISINES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Service Style</label>
          <select value={form.service_style} onChange={set("service_style")} className={inputCls} data-testid="form-service-style-select">
            {STYLES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div><label className={labelCls}>Guest Count</label><input value={form.guests} onChange={set("guests")} className={inputCls} data-testid="form-guests-input" placeholder="e.g. 50" /></div>
        <div className="sm:col-span-2">
          <label className={labelCls}>How Did You Hear About Chef Blaine?</label>
          <select value={form.referral} onChange={set("referral")} className={inputCls} data-testid="form-referral-select">
            {REFERRALS.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Tell Us About Your Event</label>
          <textarea rows={5} value={form.message} onChange={set("message")} className={inputCls} data-testid="form-message-input" placeholder="Vision, venue, budget, favorite dishes…" />
        </div>
        <div className="sm:col-span-2 text-center">
          <button type="submit" disabled={sending} data-testid="form-submit-button" className="rounded-full bg-brass px-10 py-4 text-sm font-semibold uppercase tracking-widest text-cast transition-colors hover:bg-flame hover:text-ink disabled:opacity-50">
            {sending ? "Sending…" : "Send Request"}
          </button>
        </div>
      </form>
    </section>
  );
}
